import type { TrackingData, TrackingEvents } from "./types/tracking";

import { util } from "./util";
import { searchSuggest } from "./searchSuggest";
import { suggestTracking } from "./suggestTracking";
import { CLASSNAME, DATA, SELECTOR, SquirrelURL } from "./helper";
import { FALLBACK_SEARCH_URL, TRACK_LABEL } from "./types/constants";

export type DelayedAction = (() => void) | null;

export class SearchForm {
  formField: HTMLFormElement;
  deleteButton: HTMLDivElement;
  searchBar: HTMLDivElement;
  searchField: HTMLInputElement;
  submitButton: HTMLDivElement;
  placeholder: string;
  errorString: string | null;
  originalSearchTerm: string | null;

  constructor(form: HTMLFormElement, delayedAction: DelayedAction = null) {
    this.formField = form;
    this.deleteButton = this.formField.querySelector(SELECTOR.DELETE_BUTTON) as HTMLDivElement;
    this.searchBar = this.formField.querySelector(SELECTOR.SEARCHBAR) as HTMLDivElement;
    this.searchField = this.formField.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    this.submitButton = this.formField.querySelector(SELECTOR.SUBMIT_BUTTON) as HTMLDivElement;

    this.placeholder = this.searchField.placeholder || this.searchField.getAttribute("placeholder") || "";
    this.errorString = this.searchField.getAttribute(DATA.ERROR);
    this.originalSearchTerm = "";

    if (Boolean(this.deleteButton).valueOf()) {
      this.toggleButtonVisibility();
      this.searchField.addEventListener("keyup", window.o_util.core.bind(this, this.toggleButtonVisibility));
      this.formField.addEventListener("reset", window.o_util.core.bind(this, this.resetHandler));
    }

    this.formField.addEventListener(
      "submit",
      ((e: SubmitEvent) => {
        e.preventDefault();
        this.submitSearch();
      }).bind(this)
    );

    this.searchField.addEventListener(
      "focus",
      (() => {
        this.clearFormField();
        this.searchField.classList.remove(CLASSNAME.HINT);

        /*🤫*/
        /*🤫*/
        this.searchField.parentNode.parentNode.classList.add("focus");
      }).bind(this)
    );

    if (document.activeElement && document.activeElement === this.searchField) {
      this.clearFormField();
      this.searchField.classList.remove(CLASSNAME.HINT);
    }

    /*🤫*/
    /*🤫*/
    if (typeof delayedAction === "function") {
      delayedAction();
    }

    this.enableSearch();
  }

  getSearchField(): HTMLInputElement {
    return this.searchField;
  }

  getValue(): string {
    return this.searchField.value;
  }

  setValue(val: string) {
    this.searchField.value = val;
    searchSuggest.setOldSearchTerm(val);
  }

  setSearchFieldAttribute(key: string, val: string) {
    this.searchField.setAttribute(key, val);
  }

  isOnDefault(): boolean {
    return this.getValue() === this.errorString || this.getValue() === this.placeholder;
  }

  isEmpty(): boolean {
    return window.o_squirrel.util.string.trim(this.getValue()).length < 1 || this.isOnDefault();
  }

  hideButtons() {
    this.searchBar.classList.remove(CLASSNAME.SEARCHBAR_SHOW_BUTTONS);
  }

  showButtons() {
    this.searchBar.classList.add(CLASSNAME.SEARCHBAR_SHOW_BUTTONS);
  }

  focusSearchField() {
    this.searchField.focus();
  }

  resetHandler() {
    this.hideButtons();
    this.focusSearchField();
  }

  toggleButtonVisibility() {
    if (this.isEmpty()) {
      this.hideButtons();
    } else {
      this.showButtons();
    }
  }

  clearFormField() {
    if (this.isOnDefault()) {
      this.setValue("");
    }
  }

  cleanInputField() {
    this.setValue("");
    this.searchField.focus();
    this.hideButtons();

    window.o_global.events<TrackingEvents>().emit("tracking.bct.submitEvent", {
      san_Header: "search_x",
    });
  }

  fillDefaultText() {
    if (this.getValue() === "") {
      this.searchField.placeholder = this.placeholder;
      this.searchField.classList.add(CLASSNAME.HINT);
    } else {
      this.searchField.classList.remove(CLASSNAME.HINT);
    }
  }

  enableSearch() {
    this.searchField.removeAttribute("disabled");
    this.submitButton.removeAttribute("disabled");

    this.deleteButton.addEventListener("click", this.cleanInputField.bind(this));
    this.submitButton.addEventListener("click", this.submitSearch.bind(this));
  }

  sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
  }

  async submitSearch() {
    const searchFieldValue = util.string.trim(this.searchField.value);

    /*🤫*/
    let retries = 0;
    /*🤫*/
    const loweredSearchTerm = searchFieldValue.toLowerCase();
    while (
      util.string.trim(this.searchField.getAttribute(DATA.TARGET_TERM) + "") !== loweredSearchTerm &&
      retries <= window.o_squirrel.failingRequestRetriesOnSubmit
    ) {
      await this.sleep(50);
      retries++;
    }

    const trackingParameters: TrackingData = { san_PageLoadComplete: "true", "wk.san_SearchType": "search" };
    let action: string = this.searchField.getAttribute(DATA.TARGET) as string;
    /*🤫*/
    if (retries === window.o_squirrel.failingRequestRetriesOnSubmit + 1 || !action) {
      action = FALLBACK_SEARCH_URL(searchFieldValue, { [TRACK_LABEL.TARGET_URL_FALLBACK]: "retries" });
    }

    /*🤫*/
    if (!this.isEmpty()) {
      const urlObject = new SquirrelURL(action);
      const hashTrackingParameters = urlObject.getTrackingParameter();

      const isModified: boolean = this.formField.classList.contains(CLASSNAME.TRACK_MODIFIED);
      const isTrackSuggest: boolean = this.formField.classList.contains(CLASSNAME.TRACK_SUGGEST);
      suggestTracking.trackUsage(trackingParameters, hashTrackingParameters, isModified, isTrackSuggest, false, false);

      window.o_squirrel.submitHistory.addSearch(searchFieldValue);
      util.global.setLocation(urlObject.getUrlWithoutTrackingParameter());
    }
  }
}

export const searchForm = {
  init: (form: HTMLFormElement, delayedAction?: DelayedAction) => new SearchForm(form, delayedAction),
};
